import { useState, useEffect } from 'react';
import { Flex, Text, useDisclosure } from '@chakra-ui/react';
import { differenceInDays, format } from 'date-fns';
import {
  ApplicationQuestions,
  CurrentStatus,
  Loader,
  JobStepper,
  UpcomingStatus,
  StatusTask,
} from 'components';
import { jobStatuses } from 'utils';

type StatusTask = {
  key: number;
  title: string;
  description: string;
  url?: string;
  action?: string;
};

type Status = {
  key: number;
  title: string;
  description: string;
  assigned?: string;
  updated?: string;
  tasks?: StatusTask[];
};

const UserJobStatus = ({
  loading = false,
  user = {} as UserData,
  assignment = {} as AgencyAssignment,
}: {
  loading?: boolean;
  user?: UserData | null;
  assignment?: AgencyAssignment | null;
}): JSX.Element => {
  const [step, setStep] = useState(0);
  const [current, setCurrent] = useState<Status | null>(null);
  const [next, setNext] = useState<Status | null>(null);
  const [upcoming, setUpcoming] = useState<Status[] | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [answerQuestions, setAnswerQuestions] = useState(false);

  useEffect(() => {
    const { status, statuses = [] } = assignment || ({} as AgencyAssignment);
    let currentStatus = jobStatuses.find(({ title }) => title === status);
    let upcomingSteps;
    let nextSteps;
    if (currentStatus) {
      const statusDetails = statuses.find(
        (statusObj) => statusObj.name === status,
      );
      currentStatus = { ...currentStatus, ...statusDetails };
      const { key } = currentStatus;
      const nextKey = key + 1;
      if (nextKey > jobStatuses.length) {
        upcomingSteps = null;
        nextSteps = null;
      } else {
        nextSteps = jobStatuses[key];
        upcomingSteps = jobStatuses.slice(nextKey);
      }
      setStep(currentStatus.key);
      setCurrent(currentStatus);
    } else {
      nextSteps = jobStatuses[0];
      upcomingSteps = jobStatuses.slice(1);
    }
    setUpcoming(upcomingSteps);
    setNext(nextSteps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignment]);

  const applicationQuestions = (): void => {
    setAnswerQuestions(true);
    onOpen();
  };

  const closeApplicationQuestions = (): void => {
    setAnswerQuestions(false);
    onClose();
  };

  const handleAction = (action: string): void => {
    switch (action) {
      case 'applicationQuestions':
        applicationQuestions();
        break;
      default:
        break;
    }
  };

  return (
    <Flex height="100%" justifyContent="center" bg="custom.levoOffWhite">
      {loading ? (
        <Loader />
      ) : (
        <>
          {answerQuestions && (
            <ApplicationQuestions
              open={isOpen}
              onClose={closeApplicationQuestions}
              assignment={assignment}
            />
          )}
          <Flex direction="column" width="100%" px={6} overflowY="scroll">
            <Text
              textStyle="heading2"
              color="custom.levoBlack"
              mt={8}
              mb={9}
              mx={6}
            >
              {`${user?.firstname} ${user?.lastname} Job Status`}
            </Text>
            <JobStepper
              steps={jobStatuses}
              currentStep={step}
              color="custom.levoBlue"
            />
            <Flex direction="column" mt={8}>
              <Flex direction="row" mb={3}>
                <Text textStyle="heading3">Current</Text>
                {current?.updated || assignment?.updated ? (
                  <Text textStyle="heading3" mb={3}>
                    : (Days in Status:
                    {` ${differenceInDays(
                      new Date(),
                      current?.updated
                        ? new Date(current.updated)
                        : (assignment?.updated?.seconds || 0) * 1000,
                    )}`}
                    )
                  </Text>
                ) : null}
              </Flex>
              {current ? (
                <Flex direction="column">
                  <CurrentStatus
                    title={current.title}
                    lastUpdated={format(
                      current.updated
                        ? new Date(current.updated)
                        : (assignment?.updated?.seconds || 0) * 1000,
                      'MM/dd/yyyy @ hh:mm a',
                    )}
                    text={current.description}
                  />
                  {current.tasks && (
                    <>
                      {current.tasks.map(
                        ({ key, title, description, url, action }) => (
                          <StatusTask
                            key={key}
                            title={title}
                            description={description}
                            url={url}
                            action={action}
                            handleAction={handleAction}
                          />
                        ),
                      )}
                    </>
                  )}
                </Flex>
              ) : (
                <CurrentStatus
                  title="Getting Prepped"
                  lastUpdated="N/A"
                  text="Don’t worry, we’re matching you with the right agency"
                />
              )}
            </Flex>
            <Flex direction="column" mt={8}>
              <Text textStyle="heading3" mb={3}>
                Up Next
              </Text>
              {next && (
                <Flex direction="column">
                  <UpcomingStatus
                    title={next.title}
                    text={next.title}
                    next={true}
                  />
                  {next.tasks && (
                    <>
                      {next.tasks.map(
                        ({ key, title, description, url, action }) => (
                          <StatusTask
                            key={key}
                            title={title}
                            description={description}
                            url={url}
                            action={action}
                            handleAction={handleAction}
                          />
                        ),
                      )}
                    </>
                  )}
                </Flex>
              )}
            </Flex>
            <Flex direction="column" mt={8}>
              <Text textStyle="heading3" mb={3}>
                Upcoming
              </Text>
              {upcoming && (
                <>
                  {upcoming.map(({ key, title, description, tasks }) => (
                    <Flex key={key} direction="column">
                      <UpcomingStatus title={title} text={description} />
                      {tasks && (
                        <>
                          {tasks.map(({ key, title, description, url }) => (
                            <StatusTask
                              key={key}
                              title={title}
                              description={description}
                              url={url}
                              upcoming={true}
                              handleAction={handleAction}
                            />
                          ))}
                        </>
                      )}
                    </Flex>
                  ))}
                </>
              )}
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default UserJobStatus;
